import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';
import { useNotificationStore } from '@/stores/notificationStore';
import type { Notification } from '@/types/database.types';

export function useNotifications() {
  const userId = useAuthStore((s) => s.user?.id);
  const orgId = useOrgStore((s) => s.organization?.id);
  const { notifications, unreadCount } = useNotificationStore();
  const setNotifications = useNotificationStore((s) => s.setNotifications);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const markReadInStore = useNotificationStore((s) => s.markAsRead);
  const markAllReadInStore = useNotificationStore((s) => s.markAllAsRead);
  const queryClient = useQueryClient();

  useEffect(() => {
    setNotifications([]);
  }, [userId, orgId, setNotifications]);

  const query = useQuery({
    queryKey: ['notifications', userId, orgId],
    enabled: !!userId,
    queryFn: async () => {
      let request = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId!);

      request = orgId
        ? request.or(`organization_id.eq.${orgId},organization_id.is.null`)
        : request.is('organization_id', null);

      const { data, error } = await request
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (query.data) {
      setNotifications(query.data);
    }
  }, [query.data, setNotifications]);

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const notification = payload.new as Notification;
          if (
            notification.organization_id &&
            (!orgId || notification.organization_id !== orgId)
          ) {
            return;
          }
          addNotification(notification);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, orgId, addNotification]);

  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      if (!userId) throw new Error('You must be signed in to update notifications.');

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)
        .eq('user_id', userId);
      if (error) throw error;
    },
    onMutate: (notificationId) => {
      markReadInStore(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('You must be signed in to update notifications.');

      let request = supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId!)
        .eq('is_read', false);

      request = orgId
        ? request.or(`organization_id.eq.${orgId},organization_id.is.null`)
        : request.is('organization_id', null);

      const { error } = await request;
      if (error) throw error;
    },
    onMutate: () => {
      markAllReadInStore();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return {
    notifications,
    unreadCount,
    isLoading: query.isLoading,
    markAsRead,
    markAllAsRead,
  };
}
